/**
 * Authenticated fetch wrapper.
 *
 * Auto-attaches `Authorization: Bearer <access>` from sessionStorage; if the
 * call returns 401 we call refresh() once and retry. If refresh fails, the
 * tokens are cleared and the user is redirected to the cerberus auth UI.
 */
import { CERBERUS_API_URL } from './config'
import { refreshAccessToken, shouldRefresh } from './refresh'
import { getAccessToken, setTokens } from './tokens'
import { maybeNotifyRateLimit } from '@/lib/messaging/rateLimitToast'
import type {
  LoginResponse,
  MfaChallengeResponse,
  MfaEnrollResponse,
  MfaStatusResponse,
  SignupResponse,
} from './types'

/** Typed error thrown by the cerberus auth client on non-2xx responses. */
export class CerberusError extends Error {
  readonly status: number
  readonly code?: string
  readonly body?: unknown
  constructor(
    status: number,
    message: string,
    opts: { code?: string; body?: unknown } = {},
  ) {
    super(message)
    this.name = 'CerberusError'
    this.status = status
    this.code = opts.code
    this.body = opts.body
  }
}

interface CerberusFetchOptions extends RequestInit {
  /** When true, do not auto-refresh + retry on 401. Default false. */
  noRetry?: boolean
  /** When true, do not attach an Authorization header. Default false. */
  anonymous?: boolean
  /** Override base URL (tests). */
  apiUrl?: string
  /** Injected fetch (tests). */
  fetchImpl?: typeof fetch
}

function buildUrl(path: string, apiUrl: string): string {
  if (/^https?:/i.test(path)) return path
  const base = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${base}${suffix}`
}

function withAuth(init: RequestInit, token: string): RequestInit {
  const headers = new Headers(init.headers ?? {})
  headers.set('Authorization', `Bearer ${token}`)
  return { ...init, headers }
}

export async function cerberusFetch(
  path: string,
  opts: CerberusFetchOptions = {},
): Promise<Response> {
  const {
    noRetry = false,
    anonymous = false,
    apiUrl = CERBERUS_API_URL,
    fetchImpl = fetch,
    ...init
  } = opts
  const url = buildUrl(path, apiUrl)

  if (anonymous) {
    return fetchImpl(url, init)
  }

  // Pre-emptive refresh if we know we're inside the lead window.
  if (shouldRefresh()) {
    try {
      await refreshAccessToken({ apiUrl, fetchImpl })
    } catch {
      // refresh() already cleared+redirected on terminal failure; we still
      // attempt the original request anonymously so callers see the 401.
    }
  }

  let token = getAccessToken()
  let res = token
    ? await fetchImpl(url, withAuth(init, token))
    : await fetchImpl(url, init)

  if (res.status !== 401 || noRetry) return res

  // One-shot refresh + retry on 401.
  try {
    await refreshAccessToken({ apiUrl, fetchImpl })
  } catch {
    return res
  }
  token = getAccessToken()
  if (!token) return res
  res = await fetchImpl(url, withAuth(init, token))
  return res
}

// --- Typed auth endpoints (signup / login / mfa / password reset) -----------

interface JsonInit extends Omit<RequestInit, 'body'> {
  body?: unknown
}

async function jsonRequest<T>(
  path: string,
  init: JsonInit,
  opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
): Promise<T> {
  const apiUrl = opts.apiUrl ?? CERBERUS_API_URL
  const fetchImpl = opts.fetchImpl ?? fetch
  const url = buildUrl(path, apiUrl)
  const headers = new Headers(init.headers ?? {})
  headers.set('Content-Type', 'application/json')
  headers.set('Accept', 'application/json')

  const res = await fetchImpl(url, {
    ...init,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  })

  let parsed: unknown = null
  try {
    parsed = await res.json()
  } catch {
    /* tolerate empty bodies */
  }

  if (!res.ok) {
    const errBody = (parsed ?? {}) as {
      error?: { code?: string; message?: string }
      message?: string
    }
    const message =
      errBody.error?.message ?? errBody.message ?? `HTTP ${res.status}`
    const err = new CerberusError(res.status, message, {
      code: errBody.error?.code,
      body: parsed,
    })
    // Phase 9 (M9): surface 429s through the humane rate-limit channel
    // so the user sees "Muitas tentativas — tente em alguns segundos"
    // before the throw bubbles to the boundary or the call-site catch.
    maybeNotifyRateLimit(err)
    throw err
  }
  return (parsed ?? {}) as T
}

/**
 * Bearer-authenticated JSON POST. Unlike `jsonRequest` (anonymous), this
 * routes through `cerberusFetch` so the access token is attached and a 401
 * triggers the one-shot refresh+retry. Used by the enroll/verify/disable
 * MFA-management endpoints, which require an authenticated session.
 */
async function authedJsonRequest<T>(
  path: string,
  init: JsonInit,
  opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {})
  headers.set('Content-Type', 'application/json')
  headers.set('Accept', 'application/json')

  const res = await cerberusFetch(path, {
    ...init,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    apiUrl: opts.apiUrl,
    fetchImpl: opts.fetchImpl,
  })

  let parsed: unknown = null
  try {
    parsed = await res.json()
  } catch {
    /* tolerate empty bodies */
  }

  if (!res.ok) {
    const errBody = (parsed ?? {}) as {
      error?: { code?: string; message?: string }
      message?: string
    }
    const message =
      errBody.error?.message ?? errBody.message ?? `HTTP ${res.status}`
    const err = new CerberusError(res.status, message, {
      code: errBody.error?.code,
      body: parsed,
    })
    maybeNotifyRateLimit(err)
    throw err
  }
  return (parsed ?? {}) as T
}

interface LoginArgs {
  email: string
  password: string
}

/**
 * Body for `POST /auth/login/mfa-challenge`. The endpoint is
 * UNAUTHENTICATED — the `challenge_token` (issued by `/auth/login` when
 * `mfa_required`) IS the credential. `user_id` is REQUIRED. `code` accepts
 * a 6-digit TOTP code OR a backup code.
 */
interface MfaArgs {
  challenge_token: string
  code: string
  user_id: string
}

/** Body for `POST /auth/mfa/verify` and `/auth/mfa/disable` (Bearer). */
interface MfaCodeArgs {
  code: string
}

interface SignupArgs {
  email: string
  password: string
  /**
   * Optional product-controlled URL Cerberus uses as the base for the
   * verify-email link. When omitted, Cerberus falls back to its own
   * id.francisquini.ai surface. Callers should pass an origin-qualified
   * URL pointing at *their* /verify page so the email never exposes
   * Cerberus to product end-users.
   */
  return_to?: string
  /**
   * When set (truthy non-empty string), signals waitlist-redemption to
   * Cerberus signup: the response will include `user_id` so callers can
   * finish the matching `convertEntry(...)` call on Peitho. Without it,
   * signup stays opaque (`{status:"ok"}`) for email-enumeration safety.
   * Send the same `invite` token that gated the `/entrar` page.
   */
  invite_token?: string
}

interface PasswordResetArgs {
  email: string
  /**
   * Optional product-controlled URL Cerberus uses as the base for the
   * reset email link. When omitted, Cerberus falls back to its own
   * id.francisquini.ai surface. Callers should pass an origin-qualified
   * URL pointing at *their* reset confirmation page so the email never
   * exposes Cerberus to product end-users.
   */
  return_to?: string
}

interface PasswordResetConfirmArgs {
  token: string
  user_id: string
  new_password: string
}

export const cerberusAuthClient = {
  async login(
    args: LoginArgs,
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<LoginResponse> {
    const body = await jsonRequest<LoginResponse>(
      '/auth/login',
      { method: 'POST', body: args },
      opts,
    )
    if (
      body.access_token &&
      body.refresh_token &&
      body.user_id &&
      !body.mfa_required
    ) {
      setTokens({
        access_token: body.access_token,
        refresh_token: body.refresh_token,
        user_id: body.user_id,
      })
    }
    return body
  },

  async mfaChallenge(
    args: MfaArgs,
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<MfaChallengeResponse> {
    const body = await jsonRequest<MfaChallengeResponse>(
      '/auth/login/mfa-challenge',
      { method: 'POST', body: args },
      opts,
    )
    const userId = body.user_id ?? body.user?.user_id
    if (body.access_token && body.refresh_token && userId) {
      setTokens({
        access_token: body.access_token,
        refresh_token: body.refresh_token,
        user_id: userId,
      })
    }
    return body
  },

  /**
   * `POST /auth/mfa/enroll` (Bearer). Starts TOTP enrollment and returns
   * the secret, the `otpauth_url`, a server-rendered `qr_data_url`, and the
   * one-time `backup_codes`. 403 `mfa_already_enabled` if already on.
   */
  async mfaEnroll(
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<MfaEnrollResponse> {
    return authedJsonRequest<MfaEnrollResponse>(
      '/auth/mfa/enroll',
      { method: 'POST', body: {} },
      opts,
    )
  },

  /**
   * `POST /auth/mfa/verify` (Bearer). Confirms enrollment with a TOTP code.
   * 401 `invalid_code`, 429 rate-limit.
   */
  async mfaVerify(
    args: MfaCodeArgs,
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<MfaStatusResponse> {
    return authedJsonRequest<MfaStatusResponse>(
      '/auth/mfa/verify',
      { method: 'POST', body: args },
      opts,
    )
  },

  /**
   * `POST /auth/mfa/disable` (Bearer). Turns MFA off; requires a current
   * TOTP code as proof-of-possession.
   */
  async mfaDisable(
    args: MfaCodeArgs,
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<MfaStatusResponse> {
    return authedJsonRequest<MfaStatusResponse>(
      '/auth/mfa/disable',
      { method: 'POST', body: args },
      opts,
    )
  },

  async signup(
    args: SignupArgs,
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<SignupResponse> {
    return jsonRequest<SignupResponse>(
      '/auth/signup',
      { method: 'POST', body: args },
      opts,
    )
  },

  async passwordResetRequest(
    args: PasswordResetArgs,
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<{ ok: true }> {
    await jsonRequest<unknown>(
      '/auth/password-reset/request',
      { method: 'POST', body: args },
      opts,
    )
    return { ok: true }
  },

  async passwordResetConfirm(
    args: PasswordResetConfirmArgs,
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<{ ok: true }> {
    await jsonRequest<unknown>(
      '/auth/password-reset/confirm',
      { method: 'POST', body: args },
      opts,
    )
    return { ok: true }
  },

  async verifyEmail(
    args: { token: string },
    opts: {
      apiUrl?: string
      fetchImpl?: typeof fetch
      signal?: AbortSignal
    } = {},
  ): Promise<{ status: string }> {
    const apiUrl = opts.apiUrl ?? CERBERUS_API_URL
    const fetchImpl = opts.fetchImpl ?? fetch
    const path = `/auth/verify?token=${encodeURIComponent(args.token)}`
    const url = buildUrl(path, apiUrl)
    const headers = new Headers()
    headers.set('Accept', 'application/json')
    const res = await fetchImpl(url, {
      method: 'GET',
      headers,
      signal: opts.signal,
    })
    let parsed: unknown = null
    try {
      parsed = await res.json()
    } catch {
      /* tolerate empty bodies */
    }
    if (!res.ok) {
      const errBody = (parsed ?? {}) as {
        error?: { code?: string; message?: string }
        message?: string
      }
      const message =
        errBody.error?.message ?? errBody.message ?? `HTTP ${res.status}`
      const err = new CerberusError(res.status, message, {
        code: errBody.error?.code,
        body: parsed,
      })
      maybeNotifyRateLimit(err)
      throw err
    }
    return (parsed ?? {}) as { status: string }
  },

  async logout(
    opts: { apiUrl?: string; fetchImpl?: typeof fetch } = {},
  ): Promise<{ ok: true }> {
    try {
      await jsonRequest<unknown>(
        '/auth/logout',
        { method: 'POST', body: {} },
        opts,
      )
    } catch {
      /* logout endpoint failures are non-fatal */
    }
    return { ok: true }
  },
}

